import { Banner, ChoiceList, Spinner, Text } from '@shopify/polaris';
import { useEffect, useMemo, useState } from 'react';
import { sortS3KeysPdfFirst } from '../../util/s3FileOrdering.js';
import { usePdfBlobCache } from './usePdfBlobCache';

/*
  PDF viewer that fills its parent (height: 100%) and reuses downloaded blobs. Used by the approval workspace and,
  through the PdfCard wrapper, by the detail pages.
  keys: S3 object keys of the document; selectedKey / onSelectKey: controlled selection (defaults to the first PDF).
*/
export default function PdfPane({ resource = 'invoices_in', keys = [], selectedKey, onSelectKey, cache, height = '100%', showFileChooser = true, emptyText = 'No PDF attached to this document.' }) {
  const ownCache = usePdfBlobCache(resource);
  const { load } = cache || ownCache;
  const orderedKeys = useMemo(() => sortS3KeysPdfFirst(Array.isArray(keys) ? keys : []), [keys]);
  const activeKey = selectedKey && orderedKeys.includes(selectedKey) ? selectedKey : orderedKeys[0];
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (selectedKey !== activeKey && onSelectKey && activeKey) onSelectKey(activeKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  useEffect(() => {
    let cancelled = false;
    if (!activeKey) {
      setUrl(null);
      return undefined;
    }
    setLoading(true);
    setError(false);
    load(activeKey).then((objectUrl) => {
      if (cancelled) return;
      setUrl(objectUrl);
      setError(!objectUrl);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [activeKey, load]);

  if (!orderedKeys.length) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f6f6f7', borderRadius: 8 }}>
        <Text tone="subdued">{emptyText}</Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height, minHeight: 0 }}>
      {showFileChooser && orderedKeys.length > 1 ? (
        <div style={{ marginBottom: 10 }}>
          <Banner tone="warning">Multiple files are attached to this document. Select the relevant file.</Banner>
          <ChoiceList
            title="Available files"
            titleHidden
            choices={orderedKeys.map((key) => ({ value: key, label: key.split('/').pop() }))}
            selected={[activeKey]}
            onChange={(value) => onSelectKey && onSelectKey(value[0])}
          />
        </div>
      ) : null}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {loading ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner />
          </div>
        ) : null}
        {error ? <Text tone="critical">The file could not be downloaded.</Text> : null}
        {url ? <iframe title="PDF Viewer" style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }} src={`${url}#toolbar=0&navpanes=0&view=FitH`} /> : null}
      </div>
    </div>
  );
}
